import React, {FC, useCallback, useEffect, useRef, useState} from 'react';
import {connect} from 'react-redux';
import {createSelector} from 'reselect';

import {AsyncStatusesEnum} from '../../../../libs/shared/helpers/src/types/asyncStatuses';
import {ExtendedError} from '../../../../libs/shared/helpers/src/types/customErrors';
import {openInBrowser} from '../../../../srcCommon/browser/openInBrowser';
import {UseApiState, useFetchApi, useToggle} from '../../../../srcCommon/helpers/hookHelpers';
import {reportToBugsnag} from '../../../../srcCommon/services/bugsnag/bugsnag';
import {ResolveDispatchProps} from '../../../helpers/reduxHelpers';
import {
  makeMapDispatchToProps,
  makeMapStateToProps,
  ResolveStateProps,
} from '../../../helpers/reselectHelpers';
import {ChannelModel} from '../../../models/api/channelModel';
import {ApiEmailCount, EmailCountModel} from '../../../models/api/emailCountModel';
import {
  ChannelMassArchiveActionsEnum,
  InterfaceEventsEnum,
  trackInterfaceEvent,
} from '../../../services/tracking';
import {AppState} from '../../../store/appState';
import {ThunkAsyncAction} from '../../../store/appThunk';
import {dismissChannelCanArchiveActionAsyncAction} from '../../../store/data/channels/channelsActions';
import {makeGetChannelInbox} from '../../../store/data/channels/channelsSelectors';
import {createMassArchiveJobAsyncAction} from '../../../store/data/jobs/jobsActions';
import {CutoffTimeProps, cutoffTimes} from './massArchiveCutoffDropdown';
import {MassArchiveDialog} from './massArchiveDialog';

/*
 * Props.
 */

interface MassArchiveDialogContainerProps {
  channel: ChannelModel;
}

/*
 * Selectors.
 */

function getChannel(_state: AppState, props: MassArchiveDialogContainerProps) {
  return props.channel;
}

function makeGetInboxUrl() {
  const getChannelInbox = makeGetChannelInbox(getChannel);
  return createSelector(getChannelInbox, (inbox) => {
    if (!inbox) {
      throw new Error('Inbox not found for channel.');
    }

    return inbox.url;
  });
}

/*
 * Component.
 */

type MassArchiveDialogContainerComponentProps = ResolveStateProps<typeof mapStateToProps> &
  ResolveDispatchProps<typeof mapDispatchToProps>;

const MassArchiveDialogContainerComponent: FC<MassArchiveDialogContainerComponentProps> = (props) => {
  const {inboxUrl, massArchiveAsync, dismissCanArchiveAsync} = props;
  const wasCountInitalizedRef = useRef(false);
  const [selectedCutoff, setSelectedCutoff] = useState(cutoffTimes[0]);
  const [isHidden, toggleIsHidden] = useToggle();

  /*
   * Find how many conversations can be archived.
   */

  const [emailCountResult, setEmailCountResult] = useState<UseApiState<EmailCountModel>>();
  const fetchEmailCountApi = useFetchApi({
    apiType: ApiEmailCount,
    debounceDelay: 100,
    onStateChange: (result) => {
      if (result.asyncStatus === AsyncStatusesEnum.LOADED) {
        wasCountInitalizedRef.current = true;
      }

      setEmailCountResult(result);
    },
  });

  useEffect(() => {
    fetchEmailCountApi(
      `${inboxUrl}/count_conversations?status=open&before_date=${selectedCutoff.beforeDate()}`,
    );
    // TODO: Make the result of "useFetchApi" stable to that we can properly use it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [inboxUrl, selectedCutoff]);

  /*
   * Event handlers.
   */

  const onCutoffSelect = useCallback((newCutoff: CutoffTimeProps) => {
    setSelectedCutoff(newCutoff);
  }, []);

  const onLearnMoreClick = useCallback(() => {
    openInBrowser('https://help.frontapp.com/t/8024vn/how-to-archive-a-conversation');
  }, []);

  const onMassArchiveClick = useCallback(async () => {
    toggleIsHidden();

    try {
      trackInterfaceEvent(InterfaceEventsEnum.CHANNEL_MASS_ARCHIVE_EVENT, {
        action: ChannelMassArchiveActionsEnum.DIALOG_TRIGGER,
        label: selectedCutoff.label,
      });

      await massArchiveAsync(selectedCutoff.beforeDate());
      await dismissCanArchiveAsync();
    } catch (error) {
      reportToBugsnag(new ExtendedError('Failed to trigger mass archive', error));
      toggleIsHidden();
    }
  }, [toggleIsHidden, massArchiveAsync, selectedCutoff, dismissCanArchiveAsync]);

  const onRequestClose = useCallback(async () => {
    toggleIsHidden();

    try {
      trackInterfaceEvent(InterfaceEventsEnum.CHANNEL_MASS_ARCHIVE_EVENT, {
        action: ChannelMassArchiveActionsEnum.DIALOG_DISMISS,
      });
      await dismissCanArchiveAsync();
    } catch (error) {
      reportToBugsnag(new ExtendedError('Failed to reset mass archive flag on channel.', error));
    }
  }, [toggleIsHidden, dismissCanArchiveAsync]);

  /*
   * Render.
   */

  if (!wasCountInitalizedRef.current || isHidden) {
    return null;
  }

  const emailCount = emailCountResult?.result?.count;
  return (
    <MassArchiveDialog
      emailCount={emailCount}
      selectedCutoff={selectedCutoff}
      onCutoffSelect={onCutoffSelect}
      onLearnMoreClick={onLearnMoreClick}
      onMassArchiveClick={onMassArchiveClick}
      onRequestClose={onRequestClose}
    />
  );
};

/*
 * Redux.
 */

const mapStateToProps = makeMapStateToProps<MassArchiveDialogContainerProps>()(() => ({
  inboxUrl: makeGetInboxUrl(),
}));

const mapDispatchToProps = makeMapDispatchToProps<MassArchiveDialogContainerProps>()((props) => ({
  massArchiveAsync:
    (beforeDate: number): ThunkAsyncAction =>
    (dispatch, getState) => {
      const inboxUrl = makeGetInboxUrl()(getState(), props);
      return dispatch(createMassArchiveJobAsyncAction(inboxUrl, beforeDate));
    },
  dismissCanArchiveAsync: () => dismissChannelCanArchiveActionAsyncAction(props.channel),
}));

export const MassArchiveDialogContainer = connect(
  mapStateToProps,
  mapDispatchToProps,
)(MassArchiveDialogContainerComponent);
